import { Check, ArrowRight } from "lucide-react";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AnimatedHeadline from "@/components/AnimatedHeadline";

const values = [
  { bold: "Cercanía", text: "Te escuchamos y entendemos tus necesidades reales antes de proponer cualquier solución." },
  { bold: "Claridad", text: "Explicamos todo de forma sencilla, sin tecnicismos innecesarios ni letra pequeña." },
  { bold: "Acompañamiento", text: "Estamos contigo en cada etapa de tu negocio, no solo cuando toca presentar impuestos." },
  { bold: "Confianza", text: "Tu tranquilidad es nuestra prioridad. Trabajamos para que tú puedas centrarte en lo tuyo." },
  { bold: "Trato personal", text: "Un asesor dedicado que conoce tu caso al detalle y responde cuando lo necesitas." },
];

export default function WhyChooseUs() {
  const sectionRef  = useRef(null);
  const visualRef   = useRef(null);
  const rightColRef = useRef(null);
  const valuesRef   = useRef(null);
  const ctaRef      = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Left visual — parallax as user scrolls through section
      gsap.fromTo(visualRef.current,
        { opacity: 0, x: -50 },
        { opacity: 1, x: 0, duration: 0.9, ease: "power3.out",
          scrollTrigger: { trigger: visualRef.current, start: "top 85%" } });

      gsap.to(visualRef.current, {
        y: -40,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });

      // Right column header
      gsap.fromTo(rightColRef.current,
        { opacity: 0, x: 50 },
        { opacity: 1, x: 0, duration: 0.9, ease: "power3.out",
          scrollTrigger: { trigger: rightColRef.current, start: "top 85%" } });

      // Value items — each one reveals as it enters viewport
      if (valuesRef.current) {
        Array.from(valuesRef.current.children).forEach((item, idx) => {
          gsap.fromTo(item,
            { opacity: 0, x: 40 },
            {
              opacity: 1, x: 0, duration: 0.65, ease: "power3.out",
              scrollTrigger: { trigger: item, start: "top 88%" },
              delay: idx * 0.05,
            });
        });
      }

      // CTA link
      if (ctaRef.current) {
        gsap.fromTo(ctaRef.current,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.6, ease: "power2.out",
            scrollTrigger: { trigger: ctaRef.current, start: "top 90%" } });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="nosotros"
      ref={sectionRef}
      data-testid="why-choose-section"
      className="py-28 md:py-36 bg-[#f8f6f3]">

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">

          {/* Left visual */}
          <div ref={visualRef} className="lg:col-span-5" style={{ opacity: 0 }}>
            <div className="lg:sticky lg:top-32">
              <div className="relative">
                <div className="bg-[#1a1a1a] rounded-sm relative overflow-hidden">
                  <div className="h-[2px] bg-gradient-to-r from-[#c9a96e]/0 via-[#c9a96e] to-[#c9a96e]/0" />
                  <div className="p-10 md:p-14 space-y-8">
                    <div className="absolute top-0 right-0 w-48 h-48 rounded-full border border-[#c9a96e]/[0.04] -translate-y-1/2 translate-x-1/2" />
                    <div className="relative z-10">
                      <div className="flex items-center gap-3 mb-8">
                        <div className="w-10 h-[1px] bg-[#c9a96e]/50" />
                        <span className="text-[#c9a96e] text-[10px] uppercase tracking-[0.3em] font-semibold">Nuestro compromiso</span>
                      </div>
                      <blockquote
                        className="text-[1.5rem] md:text-[1.75rem] font-light text-[#f8f6f3] leading-[1.4] italic"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        "No somos una asesoría más. Somos el socio que necesitas para que tu negocio crezca con seguridad."
                      </blockquote>
                      <div className="pt-8 mt-8 border-t border-[#c9a96e]/10">
                        <p className="text-[#c9a96e] text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>A.F. PUERTOS, S.L.</p>
                        <p className="text-[#f8f6f3]/30 text-xs mt-1.5" style={{ fontFamily: "'Manrope', sans-serif" }}>Alhaurín El Grande, Málaga</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-3 -right-3 w-full h-full border border-[#c9a96e]/10 rounded-sm -z-10" />
              </div>
            </div>
          </div>

          {/* Right column */}
          <div ref={rightColRef} className="lg:col-span-7" style={{ opacity: 0 }}>
            <span className="text-[#c9a96e] text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Por qué elegirnos
            </span>
            <AnimatedHeadline
              text="El trato que tu negocio merece"
              data-testid="why-choose-title"
              className="text-3xl sm:text-4xl md:text-[3.25rem] font-light tracking-tight mt-5 mb-7 text-[#1a1a1a] leading-[1.15]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            />
            <p className="text-[15px] text-[#666666] font-light leading-[1.8] mb-12" style={{ fontFamily: "'Manrope', sans-serif" }}>
              En A.F. Puertos —conocida también como AF Puertos o AFPuertos— creemos que
              una buena asesoría va más allá de los números. Nos implicamos en cada proyecto
              porque sabemos que detrás de cada empresa hay personas con objetivos y
              necesidades únicas.
            </p>

            <div ref={valuesRef} className="space-y-6">
              {values.map((value, idx) => (
                <div
                  key={idx}
                  data-testid={`value-item-${idx}`}
                  className="flex items-start gap-5"
                  style={{ opacity: 0 }}>
                  <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-sm bg-[#c9a96e]/10 border border-[#c9a96e]/20 flex items-center justify-center">
                    <Check className="w-3 h-3 text-[#c9a96e]" />
                  </div>
                  <p className="text-[13px] text-[#555555] font-light leading-[1.7]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    <strong className="font-semibold text-[#1a1a1a]">{value.bold}.</strong>{" "}
                    {value.text}
                  </p>
                </div>
              ))}
            </div>

            <div ref={ctaRef} className="mt-12" style={{ opacity: 0 }}>
              <a
                href="#contacto"
                onClick={(e) => { e.preventDefault(); document.querySelector("#contacto")?.scrollIntoView({ behavior: "smooth" }); }}
                className="inline-flex items-center gap-2.5 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#1a1a1a] hover:text-[#c9a96e] transition-colors duration-300 group"
                data-testid="why-choose-cta"
                style={{ fontFamily: "'Manrope', sans-serif" }}>
                <span>Contactar sin compromiso</span>
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
